/**
 * Health Screening Schemas
 *
 * Zod schemas for mother health screening profile data.
 * Used by both Vue form validation and Fastify route validation.
 */
import { z } from 'zod';

// -- Obstetric History ------------------------------------------
export const ObstetricHistorySchema = z.object({
  gravida: z.string().optional(), // Total pregnancies
  para: z.string().optional(), // Births after 20 weeks
});

export type ObstetricHistory = z.infer<typeof ObstetricHistorySchema>;

// -- Medical History --------------------------------------------
export const MedicalHistorySchema = z.object({
  medicalConditions: z.array(z.string()).optional(),
  pastMedicalHistory: z.string().optional(),
  currentMedications: z.string().optional(),
  allergies: z.string().optional(),
  historyOfMastitis: z.boolean().optional(),
});

export type MedicalHistory = z.infer<typeof MedicalHistorySchema>;

// -- Lifestyle Factors ------------------------------------------
export const LifestyleFactorsSchema = z.object({
  nicotineUse: z.enum(['yes', 'no', '']).optional(),
  alcoholUse: z.string().optional(),
  dietType: z.string().optional(),
  warning: z.string().optional(),
  notes: z.string().optional(),
});

export type LifestyleFactors = z.infer<typeof LifestyleFactorsSchema>;

// -- Infectious Disease Screening -------------------------------
export const TestResultEnum = z.enum([
  'Negative',
  'Positive',
  'Indeterminate',
  'Pending',
]);

export type TestResult = z.infer<typeof TestResultEnum>;

export const TestTypeEnum = z.enum([
  'HIV',
  'HEP A',
  'HEP B',
  'HEP C',
  'Syphilis',
  'CMV',
  'HTLV',
  'COVID-19',
  'Tuberculosis',
  'Other',
]);

export type TestType = z.infer<typeof TestTypeEnum>;

export const TestAttachmentSchema = z.object({
  id: z.string(),
  fileName: z.string(),
  fileSize: z.number(),
  uploadedAt: z.string(),
  url: z.string().optional(),
});

export type TestAttachment = z.infer<typeof TestAttachmentSchema>;

export const InfectiousDiseaseTestSchema = z.object({
  id: z.string(),
  dateOfTest: z.string(),
  testType: TestTypeEnum,
  result: TestResultEnum,
  isRecurring: z.boolean().optional(),
  nextDate: z.string().optional(),
  validityStatus: z.enum(['VALID', 'DUE_SOON', 'OVERDUE']).nullable().optional(),
  attachments: z.array(TestAttachmentSchema).optional(),
});

export type InfectiousDiseaseTest = z.infer<typeof InfectiousDiseaseTestSchema>;

// -- Combined Health Screening Data -----------------------------
export const HealthScreeningDataSchema = z.object({
  motherId: z.string(),
  obstetricHistory: ObstetricHistorySchema.optional(),
  medicalHistory: MedicalHistorySchema.optional(),
  lifestyleFactors: LifestyleFactorsSchema.optional(),
  infectiousDiseaseTests: z.array(InfectiousDiseaseTestSchema).optional(),
  lastUpdated: z.string().optional(),
});

export type HealthScreeningData = z.infer<typeof HealthScreeningDataSchema>;
